<template>
  <div class="p-8 w-full">
    <!-- Header -->
    <div class="flex items-center justify-between mb-8">
      <div>
        <h1 class="text-3xl font-bold text-gray-900">Projects</h1>
        <p class="text-gray-600 mt-1">Manage your websites and content</p>
      </div>
      
      <div class="flex items-center gap-2">
        <Button
          label="AI Onboarding"
          icon="pi pi-sparkles"
          outlined
          @click="openOnboarding"
        />
        <Button
          label="New Project"
          icon="pi pi-plus"
          @click="showCreateDialog = true"
        />
      </div>
    </div>
    
    <!-- Search and Filters -->
    <div class="mb-6 flex items-center space-x-4">
      <div class="flex-1 relative">
        <InputText 
          v-model="searchQuery"
          placeholder="Search projects..."
          class="w-full pl-10"
        />
        <i class="pi pi-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
      </div>
      
      <Select 
        v-model="statusFilter"
        :options="statusOptions"
        option-label="label"
        option-value="value"
        placeholder="All Status"
        class="w-48"
      />
    </div>
    
    <!-- Loading State -->
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <ProgressSpinner />
    </div>
    
    <!-- Error State -->
    <div v-else-if="error" class="text-center py-12">
      <i class="pi pi-exclamation-triangle text-red-500 text-4xl mb-4"></i>
      <h3 class="text-lg font-semibold text-gray-900 mb-2">Failed to load projects</h3>
      <p class="text-gray-600">{{ error.message }}</p>
    </div>
    
    <!-- Projects Grid -->
    <div v-else-if="filteredProjects.length > 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      <div
        v-for="project in filteredProjects"
        :key="project.id"
        class="project-card border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow cursor-pointer"
        role="button"
        tabindex="0"
        :aria-label="`Open project: ${project.name}`"
        @click="openProject(project)"
        @keydown.enter.prevent="openProject(project)"
        @keydown.space.prevent="openProject(project)"
      >
        <!-- Project Status Badge -->
        <div class="flex items-center justify-between mb-4">
          <Badge 
            :value="project.status"
            :severity="project.status === 'active' ? 'success' : 'secondary'"
          />
          <div class="flex items-center space-x-2">
            <Button
              icon="pi pi-external-link"
              text
              size="small"
              @click.stop="previewProject(project)"
              aria-label="Preview site"
              v-tooltip="'Preview Site'"
            />
            <Button
              icon="pi pi-ellipsis-v"
              text
              size="small"
              @click.stop="showProjectMenu(project, $event)"
              aria-label="Project menu: open, preview, settings, delete"
            />
          </div>
        </div>
        
        <!-- Project Info -->
        <div class="mb-4">
          <h3 class="text-xl font-semibold text-gray-900 mb-2">{{ project.name }}</h3>
          <p class="text-gray-600 text-sm line-clamp-2">{{ project.description || 'No description' }}</p>
        </div>
        
        <!-- Project Stats -->
        <div class="grid grid-cols-2 gap-4 mb-4 text-sm">
          <div class="text-center p-2 bg-gray-50 rounded">
            <div class="font-semibold text-gray-900">{{ getProjectStats(project).pages }}</div>
            <div class="text-gray-600">Pages</div>
          </div>
          <div class="text-center p-2 bg-gray-50 rounded">
            <div class="font-semibold text-gray-900">{{ getProjectStats(project).lastEdit }}</div>
            <div class="text-gray-600">Last Edit</div>
          </div>
        </div>
        
        <!-- Project Details -->
        <div class="text-xs text-gray-500">
          <span class="inline-block px-2 py-0.5 bg-gray-100 rounded text-gray-600">
            {{ project.config?.defaultLanguage?.toUpperCase() || 'EN' }}
          </span>
        </div>
      </div>
    </div>
    
    <!-- Empty State -->
    <div v-else class="text-center py-16">
      <i class="pi pi-folder-open text-gray-300 text-6xl mb-4"></i>
      <h3 class="text-xl font-semibold text-gray-900 mb-2">No projects found</h3>
      <p class="text-gray-600 mb-6">
        {{ searchQuery ? 'No projects match your search criteria.' : 'Create your first project to get started.' }}
      </p>
      <Button 
        label="Create Project"
        icon="pi pi-plus"
        @click="showCreateDialog = true"
      />
    </div>
    
    <!-- Project Context Menu -->
    <Menu ref="projectMenuRef" :model="projectMenuItems" :popup="true" />

    <!-- Create Project Dialog -->
    <Dialog 
      v-model:visible="showCreateDialog"
      modal
      header="Create New Project"
      class="w-full max-w-md"
    >
      <div class="space-y-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Project Name</label>
          <InputText 
            v-model="newProject.name"
            class="w-full"
            placeholder="Enter project name..."
          />
        </div>
        
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <Textarea 
            v-model="newProject.description"
            class="w-full"
            rows="3"
            placeholder="Optional description..."
          />
        </div>
        
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Site URL</label>
          <InputText 
            v-model="newProject.siteUrl"
            class="w-full"
            placeholder="https://example.com"
          />
        </div>
        
      </div>
      
      <template #footer>
        <div class="flex justify-end space-x-2">
          <Button 
            label="Cancel"
            outlined
            @click="showCreateDialog = false"
          />
          <Button 
            label="Create"
            :loading="createProjectMutation.isPending.value"
            :disabled="!newProject.name"
            @click="createProject"
          />
        </div>
      </template>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Textarea from 'primevue/textarea'
import Select from 'primevue/select'
import Dialog from 'primevue/dialog'
import Badge from 'primevue/badge'
import Menu from 'primevue/menu'
import ProgressSpinner from 'primevue/progressspinner'
import type { MenuItem } from 'primevue/menuitem'
import { useProjectsQuery, useCreateProjectMutation, useDeleteProjectMutation, type Project } from '@/composables/useProjects'
import { useConfirm } from 'primevue/useconfirm'
import { useToast } from 'primevue/usetoast'

const router = useRouter()
const confirm = useConfirm()
const toast = useToast()

const openOnboarding = () => {
  router.push('/onboarding')
}

// Query hooks
const { data: projects, isLoading, error } = useProjectsQuery()
const createProjectMutation = useCreateProjectMutation()
const deleteProjectMutation = useDeleteProjectMutation()

// Reactive data
const searchQuery = ref('')
const statusFilter = ref('all')
const showCreateDialog = ref(false)

// Context menu refs
const projectMenuRef = ref()
const selectedProject = ref<Project | null>(null)

const newProject = ref({
  name: '',
  description: '',
  siteUrl: ''
})

// Options
const statusOptions = [
  { label: 'All Projects', value: 'all' },
  { label: 'Active', value: 'active' },
  { label: 'Archived', value: 'archived' }
]

// Computed
const filteredProjects = computed(() => {
  const projectsList = projects.value ?? []
  let filtered = projectsList as Project[]
  
  // Filter by status
  if (statusFilter.value !== 'all') {
    filtered = filtered.filter((p: Project) => p.status === statusFilter.value)
  }
  
  // Filter by search query
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    filtered = filtered.filter((p: Project) => 
      p.name.toLowerCase().includes(query) ||
      p.description?.toLowerCase().includes(query) ||
      p.config?.siteUrl?.toLowerCase().includes(query)
    )
  }
  
  return filtered
})

// Methods
function formatRelativeTime(dateStr: string): string {
  const now = Date.now()
  const then = new Date(dateStr).getTime()
  const diffMs = now - then
  const diffMin = Math.floor(diffMs / 60_000)
  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return `${diffHr}h ago`
  const diffDay = Math.floor(diffHr / 24)
  if (diffDay < 30) return `${diffDay}d ago`
  const diffMonth = Math.floor(diffDay / 30)
  return `${diffMonth}mo ago`
}

const getProjectStats = (project: Project) => {
  return {
    pages: project.pages?.length ?? 0,
    lastEdit: formatRelativeTime(project.updatedAt)
  }
}

const openProject = async (project: Project) => {
  // Navigate to first page if available
  const firstPage = project.pages?.[0]
  if (firstPage) {
    router.push(`/admin/project/${project.id}/page/${firstPage.id}`)
  } else {
    // Navigate to project overview or create first page
    console.log('No pages found for project, should create first page')
    router.push(`/admin/project/${project.id}`)
  }
}

const previewProject = (project: Project) => {
  if (project.config?.siteUrl) {
    window.open(project.config.siteUrl, '_blank')
  } else {
    const websiteBase = import.meta.env.VITE_WEBSITE_URL ?? 'http://localhost:5173'
    window.open(`${websiteBase}/${project.slug}`, '_blank')
  }
}

// Context menu items
const projectMenuItems = computed<MenuItem[]>(() => [
  {
    label: 'Open Project',
    icon: 'pi pi-folder-open',
    command: () => {
      if (selectedProject.value) {
        openProject(selectedProject.value)
      }
    }
  },
  {
    label: 'Preview Site',
    icon: 'pi pi-external-link',
    command: () => {
      if (selectedProject.value) {
        previewProject(selectedProject.value)
      }
    }
  },
  {
    separator: true
  },
  {
    label: 'Project Settings',
    icon: 'pi pi-cog',
    command: () => {
      if (selectedProject.value) {
        router.push(`/admin/project/${selectedProject.value.id}/settings`)
      }
    }
  },
  {
    separator: true
  },
  {
    label: 'Delete Project',
    icon: 'pi pi-trash',
    class: 'text-red-600',
    command: () => {
      if (selectedProject.value) {
        confirmDeleteProject(selectedProject.value)
      }
    }
  }
])

const showProjectMenu = (project: Project, event: Event) => {
  selectedProject.value = project
  projectMenuRef.value?.toggle(event)
}

const confirmDeleteProject = (project: Project) => {
  confirm.require({
    message: `Are you sure you want to delete "${project.name}"? This will delete all pages and content. This action cannot be undone.`,
    header: 'Delete Project',
    icon: 'pi pi-exclamation-triangle',
    acceptClass: 'p-button-danger',
    accept: async () => {
      try {
        await deleteProjectMutation.mutateAsync(project.id)
        toast.add({
          severity: 'success',
          summary: 'Project Deleted',
          detail: `"${project.name}" has been deleted.`,
          life: 3000
        })
      } catch (err) {
        console.error('Failed to delete project:', err)
        toast.add({
          severity: 'error',
          summary: 'Delete Failed',
          detail: 'Failed to delete the project. Please try again.',
          life: 5000
        })
      }
    }
  })
}

const createProject = async () => {
  if (!newProject.value.name) return
  
  const projectData = {
    name: newProject.value.name,
    description: newProject.value.description,
    config: {
      theme: 'default',
      siteUrl: newProject.value.siteUrl || '',
      defaultLanguage: 'en',
      languages: ['en']
    }
  }
  
  try {
    const created = await createProjectMutation.mutateAsync(projectData)
    showCreateDialog.value = false
    newProject.value = {
      name: '',
      description: '',
      siteUrl: ''
    }
    
    // Navigate to new project
    openProject(created)
  } catch (err) {
    console.error('Failed to create project:', err)
  }
}

// No need for onMounted - TanStack Query handles fetching automatically
</script>

<style scoped>
.project-card:hover {
  transform: translateY(-2px);
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>